import { LogOut, ShieldOff, Sparkles, User } from 'lucide-react';
import { Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import { Logo } from '../components/Logo';
import { useBreadcrumbs } from '../lib/breadcrumbs';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LanguageToggle } from '@/components/ui/LanguageToggle';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { ThemeToggle } from '@/components/ui/ThemeToggle';

/** Mobile-only account affordance (`lg:hidden`) — on desktop, Account/Sign
 * out live in the sidebar's `SidebarUserMenu`; on mobile the sidebar is
 * replaced by `BottomNav`, so this small avatar dropdown in the topbar is
 * the only way to reach them. */
function MobileAccountMenu(): JSX.Element {
  const { client, logout, logoutAll } = useAuth();
  const navigate = useNavigate();
  const initial = client?.firstName?.[0]?.toUpperCase() ?? '?';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label="Account" className="shrink-0 rounded-full">
          <Avatar size="sm" className="bg-primary/10">
            <AvatarFallback className="bg-transparent text-primary font-display font-medium">
              {initial}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="end" className="w-60">
        <DropdownMenuItem onClick={() => navigate('/app/account')}>
          <User />
          Account
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/app/whats-new')}>
          <Sparkles />
          What’s new
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => void logout()}>
          <LogOut />
          Sign out
        </DropdownMenuItem>
        <DropdownMenuItem variant="destructive" onClick={() => void logoutAll()}>
          <ShieldOff />
          Sign out of all devices
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Slim sticky header shown at every breakpoint — carries the sidebar
 * toggle (collapses the desktop rail, DEC-0043; on mobile it opens the same
 * sidebar as a `Sheet` drawer, since `BottomNav`'s fixed 4-item tab bar
 * doesn't carry every primary nav item — e.g. Resources, P25/DEC-0092), the
 * breadcrumb trail, and the language + theme toggles; the compact logo mark
 * and account menu only appear on mobile since the desktop sidebar already
 * shows a logo mark and user menu. */
export function Topbar(): JSX.Element {
  const trail = useBreadcrumbs();

  return (
    <header className="sticky top-0 z-20 flex items-center justify-between gap-4 h-14 px-4 bg-background/85 backdrop-blur border-b border-border">
      <div className="flex items-center gap-3 min-w-0">
        <SidebarTrigger className="-ml-2" />
        <span className="lg:hidden shrink-0">
          <Logo compact />
        </span>
        <Breadcrumb>
          <BreadcrumbList className="flex-nowrap whitespace-nowrap">
            {trail.map((entry, i) => (
              <Fragment key={`${entry.label}-${i}`}>
                {i > 0 && <BreadcrumbSeparator />}
                <BreadcrumbItem>
                  {entry.to ? (
                    <BreadcrumbLink asChild>
                      <Link to={entry.to}>{entry.label}</Link>
                    </BreadcrumbLink>
                  ) : (
                    <BreadcrumbPage>{entry.label}</BreadcrumbPage>
                  )}
                </BreadcrumbItem>
              </Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <LanguageToggle />
        <ThemeToggle />
        <span className="lg:hidden">
          <MobileAccountMenu />
        </span>
      </div>
    </header>
  );
}
